/**
 * Client Registration Form (Visit Wizard)
 * Same fields and validation as the full Client page form, wrapped for use in
 * the "Add New Client" dialog during the visit wizard's client selection step.
 */

import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button } from '@/components/ui/button'
import { Loader2 } from 'lucide-react'
import { ClientCreate } from '@/types/client'
import { ClientFormFields } from '@/components/forms/ClientFormFields'
import { clientFormSchema, clientFormDefaultValues, processClientFormData, ClientFormData } from '@/lib/schemas/client-form'

interface QuickClientFormProps {
  onSubmit: (data: ClientCreate) => void
  onCancel: () => void
  isLoading?: boolean
}

export function QuickClientForm({ onSubmit, onCancel, isLoading }: QuickClientFormProps) {
  const form = useForm<ClientFormData>({
    resolver: zodResolver(clientFormSchema),
    defaultValues: clientFormDefaultValues,
  })

  const handleSubmit = (data: ClientFormData) => {
    onSubmit(processClientFormData(data) as unknown as ClientCreate)
  }

  return (
    <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
      <ClientFormFields form={form} />

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          className="bg-primary hover:bg-primary/90"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Adding Client...
            </>
          ) : (
            'Add Client'
          )}
        </Button>
      </div>
    </form>
  )
}
